import type { AppColumn } from '../../components/AppDataGrid';

/**
 * Maintenance group (G5: Maintenance ▸ Clients · Retired Clients · Contacts · Project) — row shapes, the
 * visible columns in G5's order, and the one audit FormName. The lists are served by
 * `GET /api/v1/maintenance/*`; the client / contact detail and New are the existing `/api/v1/clients`.
 */
export const MAINT_FORM_NAME = 'Client Master Data';
export const MAINT_BASE = '/api/v1/maintenance';

export type MaintenanceKind = 'clients' | 'retired' | 'contacts' | 'projects';

/** nav id → which list a Maintenance page shows. */
export const MAINT_PAGES: Record<string, MaintenanceKind> = {
  'maint-clients': 'clients',
  'maint-retired-clients': 'retired',
  'maint-contacts': 'contacts',
  'maint-projects': 'projects',
};

export interface MaintClientRow {
  oid: string;
  client: string | null; address1: string | null; address2: string | null; country: string | null;
  state: string | null; city: string | null; zip: string | null; account: string | null;
  phone: string | null; fax: string | null; webSite: string | null; mobilePhone: string | null;
  homePhone: string | null; project: string | null; displayName: string | null; owner: string | null;
  lastContactDate: string | null; contractDate: string | null; preferredContactMethod: string;
}

export interface MaintContactRow {
  oid: string; clientOid: string | null; typeCode: number;
  fullName: string | null; client: string | null; address: string | null; prospectClient: string | null;
  officePhone: string | null; mobilePhone: string | null; otherPhone: string | null; email: string | null;
  country: string | null; city: string | null; prospects: string | null; state: string | null;
  zip: string | null; jobTitle: string | null; prospect: boolean; crmProspect: string | null;
  terms: string | null; sampleReceiptNotification: boolean; reportDelivery: boolean;
  invoiceDelivery: boolean; type: string; retiredDate: string | null;
}

export interface MaintProjectRow {
  oid: string; clientOid: string | null; projectId: string | null; projectName: string | null;
  projectLocation: string | null; projectCategory: string | null; projectCode: string | null;
  client: string | null; clientAddress: string | null;
}

// G5 Customer_ListView / Customer_ListView_Retired — the visible columns, in G5's order and with G5's captions.
export const CLIENT_COLUMNS: AppColumn<MaintClientRow>[] = [
  { field: 'client', header: 'Client', width: 220 },
  { field: 'address1', header: 'Address1', width: 180 },
  { field: 'address2', header: 'Address2', width: 140 },
  { field: 'country', header: 'Country', width: 150 },
  { field: 'state', header: 'State', width: 110 },
  { field: 'city', header: 'City', width: 150 },
  { field: 'zip', header: 'Zip', width: 80 },
  { field: 'account', header: 'Account', width: 130 },
  { field: 'phone', header: 'Phone', width: 130 },
  { field: 'fax', header: 'Fax', width: 110 },
  { field: 'webSite', header: 'WebSite', width: 150 },
  { field: 'mobilePhone', header: 'MobilePhone', width: 120 },
  { field: 'homePhone', header: 'HomePhone', width: 120 },
  { field: 'project', header: 'Project', width: 150 },
  { field: 'displayName', header: 'DisplayName', width: 200 },
  { field: 'owner', header: 'Owner', width: 170 },
  { field: 'lastContactDate', header: 'LastContactDate', type: 'date', width: 130 },
  { field: 'contractDate', header: 'ContractDate', type: 'date', width: 120 },
  { field: 'preferredContactMethod', header: 'PreferredContactMethod', width: 190 },
];

const yesNo = (v: boolean) => (v ? 'Yes' : 'No');

// G5 Contact_ListView — the visible columns, in G5's order.
export const CONTACT_COLUMNS: AppColumn<MaintContactRow>[] = [
  { field: 'fullName', header: 'FullName', width: 170 },
  { field: 'client', header: 'Client', width: 220 },
  { field: 'address', header: 'Address', width: 180 },
  { field: 'prospectClient', header: 'ProspectClient', width: 150 },
  { field: 'officePhone', header: 'OfficePhone', width: 120 },
  { field: 'mobilePhone', header: 'MobilePhone', width: 120 },
  { field: 'otherPhone', header: 'OtherPhone', width: 120 },
  { field: 'email', header: 'Email', width: 230 },
  { field: 'country', header: 'Country', width: 150 },
  { field: 'city', header: 'City', width: 150 },
  { field: 'prospects', header: 'Prospects', width: 120 },
  { field: 'state', header: 'State', width: 110 },
  { field: 'zip', header: 'Zip', width: 80 },
  { field: 'jobTitle', header: 'JobTitle', width: 140 },
  { field: 'prospect', header: 'Prospect', width: 90, value: (r) => yesNo(r.prospect) },
  { field: 'crmProspect', header: 'Client', width: 140 },
  { field: 'terms', header: 'Terms', width: 100 },
  { field: 'sampleReceiptNotification', header: 'SampleReceiptNotification', width: 190, value: (r) => yesNo(r.sampleReceiptNotification) },
  { field: 'reportDelivery', header: 'ReportDelivery', width: 120, value: (r) => yesNo(r.reportDelivery) },
  { field: 'invoiceDelivery', header: 'InvoiceDelivery', width: 120, value: (r) => yesNo(r.invoiceDelivery) },
  { field: 'type', header: 'Type', width: 130 },
  { field: 'retiredDate', header: 'RetiredDate', type: 'date', width: 120 },
];

// G5 Project_ListView — the visible columns, in G5's order and with G5's captions.
export const PROJECT_COLUMNS: AppColumn<MaintProjectRow>[] = [
  { field: 'projectId', header: 'ProjectID', width: 120 },
  { field: 'projectName', header: 'ProjectName', width: 240 },
  { field: 'projectLocation', header: 'ProjectLocation', width: 200 },
  { field: 'projectCategory', header: 'ProjectCategory', width: 180 },
  { field: 'projectCode', header: 'ProjectCode', width: 120 },
  { field: 'client', header: 'Client', width: 240 },
  { field: 'clientAddress', header: 'ClientAddress', width: 220 },
];

/** Case-insensitive match of the search box against every shown column. */
export function matchesSearch<T>(row: T, columns: AppColumn<T>[], search: string): boolean {
  const q = search.trim().toLowerCase();
  if (!q) return true;
  return columns.some((c) => {
    const raw = c.value ? c.value(row) : (row as Record<string, unknown>)[c.field];
    return raw != null && String(raw).toLowerCase().includes(q);
  });
}
